export type FirmNameRecord = {
  id?: string;
  name?: string | null;
  firmName?: string | null;
};

export const FIRM_NAME_ALREADY_EXISTS_MESSAGE =
  "A firm with this name already exists.";

export function normalizeFirmNameKey(value: string): string {
  return value.trim().toLowerCase();
}

export function buildFirmNameIndex(firms: FirmNameRecord[]): Set<string> {
  const index = new Set<string>();
  for (const firm of firms) {
    for (const field of [firm.name, firm.firmName]) {
      const key = normalizeFirmNameKey(String(field ?? ""));
      if (key) {
        index.add(key);
      }
    }
  }
  return index;
}

export function isFirmNameTaken(
  value: string,
  index: Set<string>,
  excludeFirmId?: string,
  firms?: FirmNameRecord[],
): boolean {
  const key = normalizeFirmNameKey(value);
  if (!key) {
    return false;
  }

  if (excludeFirmId && firms?.length) {
    for (const firm of firms) {
      if (firm.id === excludeFirmId) {
        continue;
      }
      for (const field of [firm.name, firm.firmName]) {
        if (normalizeFirmNameKey(String(field ?? "")) === key) {
          return true;
        }
      }
    }
    return false;
  }

  return index.has(key);
}

export function getCreateFirmNameConflicts(
  form: { firmName: string; displayName: string },
  index: Set<string>,
): { firmName?: string; displayName?: string } {
  const errors: { firmName?: string; displayName?: string } = {};
  const firmName = form.firmName.trim();
  const displayName = form.displayName.trim();

  if (isFirmNameTaken(firmName, index)) {
    errors.firmName = FIRM_NAME_ALREADY_EXISTS_MESSAGE;
  }

  if (
    displayName &&
    normalizeFirmNameKey(displayName) !== normalizeFirmNameKey(firmName) &&
    isFirmNameTaken(displayName, index)
  ) {
    errors.displayName = FIRM_NAME_ALREADY_EXISTS_MESSAGE;
  }

  return errors;
}

export function getFirmDuplicateFieldErrors(
  error: unknown,
): { firmName?: string; displayName?: string } {
  const message = extractDuplicateErrorMessage(error);
  if (!message || !/duplicate/i.test(message)) {
    return {};
  }

  const errors: { firmName?: string; displayName?: string } = {};
  const mentionsFirmName = /firmName/i.test(message);
  const mentionsName = /\bname\b/i.test(message);

  if (mentionsFirmName) {
    errors.firmName = FIRM_NAME_ALREADY_EXISTS_MESSAGE;
  }
  if (mentionsName && !mentionsFirmName) {
    errors.displayName = FIRM_NAME_ALREADY_EXISTS_MESSAGE;
  }
  if (!mentionsFirmName && !mentionsName) {
    errors.firmName = FIRM_NAME_ALREADY_EXISTS_MESSAGE;
  }

  return errors;
}

function extractDuplicateErrorMessage(error: unknown): string {
  if (!error || typeof error !== "object") {
    return "";
  }
  const maybeError = error as {
    message?: string;
    response?: { data?: unknown };
  };

  const data = maybeError.response?.data;
  if (typeof data === "string") {
    return data;
  }
  if (data && typeof data === "object") {
    const record = data as { message?: string; error?: string };
    if (typeof record.message === "string") {
      return record.message;
    }
    if (typeof record.error === "string") {
      return record.error;
    }
  }
  if (typeof maybeError.message === "string") {
    return maybeError.message;
  }
  return "";
}
